import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, ArrowRight, ArrowLeft, Sparkles, X, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useOrgSettings, OnboardingPayload } from "@/hooks/useOrgSettings";
import {
  UNIVERSAL_STEPS,
  getAllSteps,
  US_STATES,
  type OnboardingStep,
} from "@/lib/onboardingBranches";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onClose: () => void;
  required?: boolean;
}

type Answers = {
  org_type?: string;
  org_state?: string;
  org_scale?: number;
  avg_annual_salary?: number;
  annual_budget?: number | null;
  primary_goal?: string;
  branch_answers: Record<string, any>;
};

function getValue(answers: Answers, step: OnboardingStep) {
  return step.field.branchSection
    ? answers.branch_answers?.[step.field.key]
    : (answers as any)[step.field.key];
}

function setValue(answers: Answers, step: OnboardingStep, value: any): Answers {
  if (step.field.branchSection) {
    return {
      ...answers,
      branch_answers: { ...answers.branch_answers, [step.field.key]: value },
    };
  }
  return { ...answers, [step.field.key]: value };
}

function suggestedDefault(step: OnboardingStep, orgType?: string): number | undefined {
  if (step.field.defaultValue !== undefined) return step.field.defaultValue;
  if (step.field.defaultBy && orgType) return step.field.defaultBy[orgType];
  return undefined;
}

export function OnboardingWizard({ open, onClose, required = false }: Props) {
  const { saveOnboarding, isSaving, skipOnboarding } = useOrgSettings();
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({ branch_answers: {} });
  const [submitting, setSubmitting] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  useEffect(() => {
    if (open) {
      setStepIndex(0);
      setAnswers({ branch_answers: {} });
      setSubmitting(false);
      setShowSkipConfirm(false);
    }
  }, [open]);

  const allSteps = useMemo(() => getAllSteps(answers), [answers]);
  const currentStep = allSteps[stepIndex];
  const totalSteps = allSteps.length;
  const isLast = stepIndex === totalSteps - 1;
  const progress = ((stepIndex + 1) / totalSteps) * 100;

  const value = currentStep ? getValue(answers, currentStep) : undefined;
  const required_ = currentStep?.field.required;
  const ack = currentStep?.acknowledgment?.(answers);

  const canAdvance = useMemo(() => {
    if (!currentStep) return false;
    if (!required_) return true;
    if (currentStep.field.type === "yesno") return value === true || value === false;
    if (currentStep.field.type === "text") return typeof value === "string" && value.trim().length > 0;
    if (currentStep.field.type === "select" || currentStep.field.type === "state")
      return !!value;
    return value !== undefined && value !== null && value !== "" && !Number.isNaN(value);
  }, [currentStep, value, required_]);

  const handleChange = (v: any) => {
    if (!currentStep) return;
    setAnswers((a) => setValue(a, currentStep, v));
  };

  const handleSkipField = () => {
    if (!currentStep) return;
    setAnswers((a) => setValue(a, currentStep, null));
    if (isLast) handleSubmit();
    else setStepIndex((i) => i + 1);
  };

  const handleNext = () => {
    if (isLast) handleSubmit();
    else setStepIndex((i) => i + 1);
  };

  const handleBack = () => setStepIndex((i) => Math.max(0, i - 1));

  const handleSkipAll = async () => {
    try {
      await skipOnboarding();
      toast.message("Setup skipped", {
        description: "Using generic defaults. You can calibrate any time from Org Profile.",
      });
      onClose();
    } catch (e) {
      toast.error("Couldn't skip — try again");
    }
  };

  const handleSubmit = async () => {
    if (!answers.org_type || !answers.org_state || !answers.org_scale ||
        !answers.avg_annual_salary || !answers.primary_goal) {
      toast.error("Please complete the required questions before finishing.");
      return;
    }
    setSubmitting(true);
    try {
      await saveOnboarding({
        org_type: answers.org_type,
        org_state: answers.org_state,
        org_scale: answers.org_scale,
        avg_annual_salary: answers.avg_annual_salary,
        annual_budget: answers.annual_budget ?? null,
        primary_goal: answers.primary_goal,
        branch_answers: answers.branch_answers,
      } as OnboardingPayload);
      toast.success("Multipliers calibrated for your organization");
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Calibration failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentStep) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden gap-0"
      >
        {/* Header with progress + skip-all */}
        <div className="px-8 pt-6 pb-4 border-b border-border bg-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
                Calibrate Upbrief for your organization
              </span>
            </div>
            <button
              onClick={() => setShowSkipConfirm(true)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              disabled={submitting || isSaving}
            >
              Skip entire setup
            </button>
          </div>
          <div className="flex items-center gap-3">
            <Progress value={progress} className="h-1.5 flex-1" />
            <span className="text-xs text-muted-foreground tabular-nums shrink-0">
              {stepIndex + 1} of {totalSteps}
            </span>
          </div>
        </div>

        {/* Skip-all confirmation overlay */}
        {showSkipConfirm && (
          <div className="absolute inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-8">
            <div className="max-w-md text-center space-y-4">
              <h3 className="text-lg font-semibold">Skip onboarding?</h3>
              <p className="text-sm text-muted-foreground">
                Upbrief will use generic defaults. Your value calculations may be inaccurate
                until you calibrate. You can come back to this any time from Org Profile.
              </p>
              <div className="flex items-center justify-center gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setShowSkipConfirm(false)}>
                  Keep going
                </Button>
                <Button variant="destructive" size="sm" onClick={handleSkipAll}>
                  Skip for now
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Calibration in-progress */}
        {(submitting || isSaving) ? (
          <div className="px-8 py-16 flex flex-col items-center text-center gap-4 min-h-[420px] justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <h3 className="text-lg font-semibold">Calibrating your multipliers…</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Our AI is reasoning about your organization to generate intelligent, defensible
              dollar values for every metric. This usually takes 10–15 seconds.
            </p>
          </div>
        ) : (
          <div className="px-8 py-8 min-h-[420px] flex flex-col">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="flex-1 flex flex-col"
              >
                {ack && (
                  <p className="text-sm text-primary mb-2 font-medium">{ack}</p>
                )}
                <Label className="text-xl font-semibold leading-snug mb-1">
                  {currentStep.field.label}
                </Label>
                {currentStep.field.helper && (
                  <p className="text-sm text-muted-foreground mb-6">{currentStep.field.helper}</p>
                )}
                {!currentStep.field.helper && <div className="mb-6" />}

                <FieldRenderer
                  step={currentStep}
                  value={value}
                  onChange={handleChange}
                  orgType={answers.org_type}
                />

                {!required_ && (
                  <button
                    onClick={handleSkipField}
                    className="self-start mt-4 text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
                  >
                    Skip for now
                  </button>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Footer nav */}
            <div className="flex items-center justify-between pt-8 mt-auto border-t border-border -mx-8 px-8">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                disabled={stepIndex === 0}
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Back
              </Button>
              <Button onClick={handleNext} disabled={!canAdvance} size="sm">
                {isLast ? (
                  <>
                    <Check className="w-4 h-4 mr-1.5" />
                    Finish & calibrate
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FieldRenderer({
  step,
  value,
  onChange,
  orgType,
}: {
  step: OnboardingStep;
  value: any;
  onChange: (v: any) => void;
  orgType?: string;
}) {
  const f = step.field;
  const suggestion = suggestedDefault(step, orgType);

  if (f.type === "select") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {f.options?.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={cn(
              "text-left px-4 py-3 rounded-lg border text-sm font-medium transition-all",
              value === opt
                ? "border-primary bg-primary/5 ring-1 ring-primary"
                : "border-border hover:border-foreground/30 hover:bg-muted/50",
            )}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }

  if (f.type === "state") {
    return (
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger className="h-11">
          <SelectValue placeholder="Select your state" />
        </SelectTrigger>
        <SelectContent>
          {US_STATES.map((s) => (
            <SelectItem key={s.code} value={s.code}>
              {s.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (f.type === "yesno") {
    return (
      <div className="flex gap-2">
        {[
          { v: true, label: "Yes" },
          { v: false, label: "No" },
        ].map((opt) => (
          <button
            key={opt.label}
            type="button"
            onClick={() => onChange(opt.v)}
            className={cn(
              "flex-1 px-4 py-3 rounded-lg border text-sm font-medium transition-all",
              value === opt.v
                ? "border-primary bg-primary/5 ring-1 ring-primary"
                : "border-border hover:border-foreground/30 hover:bg-muted/50",
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    );
  }

  if (f.type === "text") {
    return (
      <Textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Type your answer…"
        className="min-h-24"
      />
    );
  }

  // number / currency / percent
  const prefix = f.type === "currency" ? "$" : null;
  const suffix = f.type === "percent" ? "%" : null;

  return (
    <div className="space-y-2">
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            {prefix}
          </span>
        )}
        <Input
          type="number"
          inputMode="decimal"
          min={0}
          step={f.type === "percent" ? "0.1" : f.type === "currency" ? "100" : "1"}
          value={value ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") onChange(undefined);
            else onChange(parseFloat(raw));
          }}
          placeholder={suggestion !== undefined ? `Suggested: ${suggestion.toLocaleString()}` : ""}
          className={cn("h-11", prefix && "pl-7", suffix && "pr-8")}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            {suffix}
          </span>
        )}
      </div>
      {suggestion !== undefined && value === undefined && (
        <button
          type="button"
          onClick={() => onChange(suggestion)}
          className="text-xs text-primary hover:underline"
        >
          Use suggested {prefix}{suggestion.toLocaleString()}{suffix ?? ""}
        </button>
      )}
    </div>
  );
}
